/**
 * Transition Rules: EnergyPlus 9.0 to 9.1
 */

import type { VersionTransition, ObjectTransitionRule, MultiObjectResult } from '../types';
import type { IDFObject, IDFDocument } from '../../parser/types';
import { createCopyMappings, createShiftedCopyMappings } from '../types';
import { copyField, createObject, addChange } from '../engine';

export function createTransition_9_0_to_9_1(): VersionTransition {
  const objectRules = new Map<string, ObjectTransitionRule>();
  const variableMappings = new Map<string, string>();
  const deletedObjects = new Set<string>();
  const renamedObjects = new Map<string, string>();

  // HybridModel:Zone - Insert multiple fields
  // Fields 1-4 unchanged, insert 'No' at 5, blank fields 6-16, original 6-9 → 17-20
  objectRules.set('HYBRIDMODEL:ZONE', {
    sourceClass: 'HybridModel:Zone',
    targetClass: 'HybridModel:Zone',
    fieldMappings: [
      ...createCopyMappings(0, 4), // Fields 0-3 (1-4 in 1-based)
      { sourceIndex: -1, targetIndex: 4, type: 'add_default', defaultValue: 'No' },
      // Insert blank fields 5-15 (indices 5-15)
      ...Array.from({ length: 11 }, (_, i) => ({
        sourceIndex: -1,
        targetIndex: 5 + i,
        type: 'add_default' as const,
        defaultValue: '',
      })),
      // Original fields 4-7 (indices 4-7) → 16-19
      ...createShiftedCopyMappings(4, 16, 4),
    ],
  });

  // ZoneHVAC:EquipmentList - Equipment entries expand from 4 to 6 fields each
  // Old format: Name, Load Distribution Scheme, then repeating (Object Type, Object Name, Cooling Seq, Heating Seq)
  // New format: Same but adds Cooling Fraction, Heating Fraction (empty defaults) per equipment
  objectRules.set('ZONEHVAC:EQUIPMENTLIST', {
    sourceClass: 'ZoneHVAC:EquipmentList',
    targetClass: 'ZoneHVAC:EquipmentList',
    fieldMappings: [], // Using multiObjectTransform instead
    multiObjectTransform: (object: IDFObject, _document: IDFDocument): MultiObjectResult => {
      const result: MultiObjectResult = {
        primary: null,
        additional: [],
        changes: [],
        warnings: [],
      };

      const newFields = [
        copyField(object.fields[0]), // Name
        copyField(object.fields[1]), // Load Distribution Scheme
      ];

      // Process equipment entries - each group of 4 fields becomes 6 fields
      const equipmentStartIndex = 2;
      const oldFieldsPerEquipment = 4;
      const numEquipment = Math.floor((object.fields.length - equipmentStartIndex) / oldFieldsPerEquipment);

      for (let i = 0; i < numEquipment; i++) {
        const sourceStart = equipmentStartIndex + i * oldFieldsPerEquipment;
        // Copy the original 4 fields
        for (let j = 0; j < oldFieldsPerEquipment; j++) {
          newFields.push(copyField(object.fields[sourceStart + j]));
        }
        // Add the 2 new fields (Cooling Fraction, Heating Fraction) with empty defaults
        newFields.push({ value: '' }); // Cooling Fraction Schedule Name
        newFields.push({ value: '' }); // Heating or No-Load Fraction Schedule Name
      }

      if (numEquipment > 0) {
        addChange(
          result.changes,
          'field_added',
          object.className,
          `Expanded ${String(numEquipment)} equipment entries from 4 to 6 fields each`,
          object.fields[0]?.value
        );
      }

      result.primary = createObject(
        object.id,
        object.className,
        newFields,
        [...object.leadingComments],
        object.sourceLocation
      );

      return result;
    },
  });

  return {
    fromVersion: { major: 9, minor: 0 },
    toVersion: { major: 9, minor: 1 },
    objectRules,
    variableMappings,
    deletedObjects,
    renamedObjects,
  };
}
